/**
 * Combobox — free-text input + suggestion panel, for a value that can
 * legitimately be typed fresh (today: `season`, which seeds to ZERO values
 * on a fresh install — see `ReferenceValueCombobox`). Unlike `Select`, this
 * is never a closed list: typing an entirely new value always works,
 * regardless of what (if anything) the suggestion panel offers.
 *
 * Built on `@radix-ui/react-popover` for positioning/portal/outside-click
 * dismissal only — the combobox ARIA pattern (role, aria-activedescendant,
 * arrow-key highlight) is the small amount of glue Radix doesn't ship a
 * dedicated primitive for. The visible control is a plain `<input>`, not a
 * wrapped custom element, so it behaves exactly like any other text field
 * (forms, autofill, screen readers) with a styled suggestion panel layered
 * on top — same desktop-popover/phone-bottom-sheet treatment as `Select`
 * (see `.pma-combobox-*` in `index.css`).
 *
 *   <Combobox
 *     value={season}
 *     onChange={setSeason}
 *     options={activeSeasons}
 *     placeholder="e.g. 2025/26"
 *     emptyMessage="No seasons yet — type a new one"
 *   />
 */

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "../../lib/utils";
import { matchesQuery } from "./selectFilter";

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  /** Suggestions only — never constrains what can be typed/submitted. */
  options: ComboboxOption[];
  id?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  /** Shown in the suggestion panel when nothing matches (or the category
   *  has zero values at all) — the fresh-install case this component
   *  exists for must read as an invitation to type, not a dead end. */
  emptyMessage?: string;
  "data-testid"?: string;
}

export function Combobox({
  value,
  onChange,
  options,
  id,
  name,
  placeholder,
  disabled,
  required,
  className,
  emptyMessage,
  "data-testid": dataTestId,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [highlighted, setHighlighted] = React.useState(-1);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Same matching rule as `Select`'s `useSelectFilter` (case-insensitive,
  // trimmed, accent-insensitive) — the one sanctioned filter for this
  // primitive family, see `selectFilter.ts`.
  const filtered = value.trim()
    ? options.filter(
        (option) =>
          matchesQuery(option.label, value) ||
          matchesQuery(option.value, value),
      )
    : options;

  const listId = id ? `${id}-suggestions` : undefined;

  const commit = (option: ComboboxOption) => {
    onChange(option.value);
    setOpen(false);
    setHighlighted(-1);
    inputRef.current?.focus();
  };

  return (
    <PopoverPrimitive.Root open={open && !disabled} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-activedescendant={
            highlighted >= 0 && filtered[highlighted]
              ? `${listId}-option-${highlighted}`
              : undefined
          }
          autoComplete="off"
          required={required}
          disabled={disabled}
          value={value}
          data-testid={dataTestId}
          placeholder={placeholder}
          className={cn("input", className)}
          onChange={(event) => {
            onChange(event.target.value);
            setHighlighted(-1);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setOpen(true);
              setHighlighted((current) =>
                filtered.length === 0 ? -1 : (current + 1) % filtered.length,
              );
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setOpen(true);
              setHighlighted((current) =>
                filtered.length === 0
                  ? -1
                  : current <= 0
                    ? filtered.length - 1
                    : current - 1,
              );
            } else if (event.key === "Enter") {
              if (open && highlighted >= 0 && filtered[highlighted]) {
                event.preventDefault();
                commit(filtered[highlighted]);
              }
            } else if (event.key === "Escape" && open) {
              event.preventDefault();
              setOpen(false);
              setHighlighted(-1);
            }
          }}
        />
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        {/* Radix's Popover.Portal forwards its children with `asChild` onto
            the underlying portal node, which requires exactly one element —
            the backdrop and Content are grouped under one wrapper div so
            Portal still only ever sees a single child. */}
        <div>
          <div className="pma-combobox-backdrop" aria-hidden="true" />
          <PopoverPrimitive.Content
            data-pma-combobox-content=""
            align="start"
            sideOffset={6}
            collisionPadding={8}
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            className={cn(
              "pma-combobox-content z-50 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg",
              "min-w-[var(--radix-popover-trigger-width)] max-h-[min(20rem,var(--radix-popover-content-available-height))]",
            )}
          >
            <ul
              id={listId}
              role="listbox"
              aria-label={placeholder}
              className="p-1.5"
            >
              {filtered.length === 0 ? (
                <li
                  role="presentation"
                  className="select-none px-3 py-4 text-center text-sm text-gray-400"
                >
                  {emptyMessage}
                </li>
              ) : (
                filtered.map((option, index) => (
                  <li
                    key={option.value}
                    id={listId ? `${listId}-option-${index}` : undefined}
                    role="option"
                    aria-selected={option.value === value}
                    className={cn(
                      "cursor-pointer select-none rounded-lg px-3 py-2 text-sm text-gray-700",
                      index === highlighted &&
                        "bg-primary-50 text-primary-900 shadow-[inset_3px_0_0_theme(colors.primary.500)]",
                    )}
                    onMouseEnter={() => setHighlighted(index)}
                    onMouseDown={(event) => {
                      // Prevent the input from blurring before onChange commits.
                      event.preventDefault();
                      commit(option);
                    }}
                  >
                    {option.label}
                  </li>
                ))
              )}
            </ul>
          </PopoverPrimitive.Content>
        </div>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
